import axios from 'axios';
import { AuthProvider } from 'react-admin';
import API_ROUTES from '../routes/routes'; 
import { UserIdentity, UserRole } from '../types/types';
import { isTokenExpired, isTokenExpiringSoon } from '../utils/jwtUtils'; 

const authProvider: AuthProvider = {
    login: async ({ username, password }) => {
        // Clear any existing session before attempting a new login
        localStorage.clear();
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}${API_ROUTES.login}`, { username, password });
            const { token, user } = response.data;
            localStorage.setItem('user_authToken', token);
            localStorage.setItem('user_id', user.id);
            localStorage.setItem('user_name', user.user_name);
            localStorage.setItem('user_fullName', `${user.user_first_name} ${user.user_last_name}`);
            localStorage.setItem('user_role_id', user.role_id);
            localStorage.setItem('user_role', user.role);
            // Store username for potential token renewal (not password for security)
            localStorage.setItem('user_username', username);

        } catch (error) {
            console.error('Login error:', error);
            throw new Error('Login failed');
        }
    },

    logout: () => {
        localStorage.clear();
        return Promise.resolve();
    },
    checkError: (error) => {
        // Handle 401 (Unauthorized) - token expired or invalid
        // Check both error.status (fetchUtils format) and error.response?.status (axios format)
        const status = error.status || error.response?.status;
        if (status === 401 || status === 403) {
            console.log('Authentication error (401/403) - clearing session and redirecting to login');
            localStorage.clear();
            return Promise.reject();
        }
        // Network errors (no response) should not trigger logout
        if (!error.response && !error.status) { 
            console.error('Network error:', error);
            return Promise.resolve(); // to not log out
        }
        return Promise.resolve();
    },    
    checkAuth: () => {
        const token = localStorage.getItem('user_authToken');
        
        if (!token) {
            return Promise.reject();
        }
        
        // Check if token is expired
        if (isTokenExpired(token)) {
            console.log('Token expired - clearing session');
            localStorage.clear();
            return Promise.reject();
        }
        
        // Check if token is expiring soon (within 5 minutes)
        // Note: We can't auto-renew without password, so we'll let it expire naturally
        // The user will be redirected to login when token actually expires
        if (isTokenExpiringSoon(token, 5)) {
            console.log('Token expiring soon (within 5 minutes) - will require login on next request');
        }
        
        return Promise.resolve();
    },
    getPermissions: () => {
        const role = localStorage.getItem('user_role');
        return Promise.resolve(role);
    },
    getIdentity: () => {
        const id = localStorage.getItem('user_id') || '';
        const userName = localStorage.getItem('user_name') || '';
        const fullName = localStorage.getItem('user_fullName') || '';
        const role_id = localStorage.getItem('user_role_id') || '';
        const defaultRoleId = UserRole.Admin; 
        const convertedRoleId = role_id ? parseInt(role_id) as UserRole : defaultRoleId;
        const role = localStorage.getItem('user_role') || '';
        const identity: UserIdentity = { id, fullName, role_id: convertedRoleId, role, avatar: '' }; // Add avatar if available
        return Promise.resolve(identity);

    },
};
const getUserRole = (): UserRole | null => {
    const role_id = localStorage.getItem('user_role_id');
    return role_id ? parseInt(role_id) as UserRole : null;
};

const isRoleAuthorized = (allowedRoles: UserRole[]): boolean => {
    const userRole = getUserRole();
    return userRole ? allowedRoles.includes(userRole) : false;
};

authProvider.getUserRole = getUserRole;
authProvider.isRoleAuthorized = isRoleAuthorized;

export default authProvider;